import { max, parseISO, subDays, subMinutes } from "date-fns";
import { z } from "zod";

export const SAME_VISIT_MINUTES = 30;
export const FALLBACK_DAYS = 7;
export const MAX_LAST_VISIT_DAYS = 30;

export const storedLastVisitSchema = z.object({
    /** When the user last opened the page. */
    lastSeenAt: z.string().datetime(),
    /** Where that visit's range started, kept while the visit lasts. */
    since: z.string().datetime(),
});

export type StoredLastVisit = z.infer<typeof storedLastVisitSchema>;

/**
 * Where "Since your last visit" starts. Reopening the page within half an
 * hour keeps the same start, so a reload doesn't empty the page. No stored
 * visit falls back to 7 days; a start older than 30 days is capped there.
 */
export function resolveLastVisit({
    stored,
    now,
}: {
    stored: StoredLastVisit | undefined;
    now: Date;
}): Date {
    if (!stored) {
        return subDays(now, FALLBACK_DAYS);
    }
    const lastSeenAt = parseISO(stored.lastSeenAt);
    const isSameVisit = lastSeenAt > subMinutes(now, SAME_VISIT_MINUTES);
    const earliest = subDays(now, MAX_LAST_VISIT_DAYS);
    return max([isSameVisit ? parseISO(stored.since) : lastSeenAt, earliest]);
}
